import React from 'react';
import PropTypes from 'prop-types';
import {Provider} from 'react-redux';
import {createStore, combineReducers, compose} from 'redux';
import ConnectedIntlProvider from './connected-intl-provider.jsx';

import localesReducer, {initLocale, localesInitialState} from '../reducers/locales';

import {setPlayer, setFullScreen} from '../reducers/mode.js';

import locales from 'scratch-l10n';
import {detectLocale} from './detect-locale';
import {getLoginError, getNoSession, sessionInitialState, setSession} from '../reducers/session';
import api from './api';
import {setProjectId} from '../reducers/project-state';

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

/*
 * Higher Order Component to provide redux state. If an `intl` prop is provided
 * it will override the internal `intl` redux state
 * @param {React.Component} WrappedComponent - component to provide state for
 * @param {boolean} localesOnly - only provide the locale state, not everything
 *                      required by the GUI. Used to exclude excess state when
                        only rendering modals, not the GUI.
 * @returns {React.Component} component with redux and intl state provided
 */
const AppStateHOC = function (WrappedComponent, localesOnly) {
    class AppStateWrapper extends React.Component {
        constructor (props) {
            super(props);
            let initialState = {};
            let reducers = {};
            let enhancer;

            let initializedLocales = localesInitialState;
            const locale = detectLocale(Object.keys(locales));
            if (locale !== 'en') {
                initializedLocales = initLocale(initializedLocales, locale);
            }
            if (localesOnly) {
                // Used for instantiating minimal state for the unsupported
                // browser modal
                reducers = {locales: localesReducer};
                initialState = {locales: initializedLocales};
                enhancer = composeEnhancers();
            } else {
                // You are right, this is gross. But it's necessary to avoid
                // importing unneeded code that will crash unsupported browsers.
                const guiRedux = require('../reducers/gui');
                // const sessionRedux = require('../reducers/session');
                // const sessionReducer = sessionRedux.default;
                const guiReducer = guiRedux.default;
                const {
                    guiInitialState,
                    guiMiddleware,
                    initFullScreen,
                    initPlayer,
                    initTelemetryModal
                } = guiRedux;
                const {ScratchPaintReducer} = require('scratch-paint');

                let initializedGui = guiInitialState;
                if (props.isFullScreen || props.isPlayerOnly) {
                    if (props.isFullScreen) {
                        initializedGui = initFullScreen(initializedGui);
                    }
                    if (props.isPlayerOnly) {
                        initializedGui = initPlayer(initializedGui);
                    }
                } else if (props.showTelemetryModal) {
                    initializedGui = initTelemetryModal(initializedGui);
                }

                reducers = {
                    locales: localesReducer,
                    scratchGui: guiReducer,
                    scratchPaint: ScratchPaintReducer
                };

                initialState = {
                    locales: initializedLocales,
                    scratchGui: initializedGui
                };
                enhancer = composeEnhancers(guiMiddleware);
            }
            const reducer = combineReducers(reducers);
            this.store = createStore(
                reducer,
                initialState,
                enhancer
            );
        }

        componentDidMount () {
            // Ensure isPlayerOnly and isFullScreen are set on mount
            if (!localesOnly) {
                if (this.props.isPlayerOnly !== undefined) {
                    this.store.dispatch(setPlayer(this.props.isPlayerOnly));
                }
                if (this.props.isFullScreen !== undefined) {
                    this.store.dispatch(setFullScreen(this.props.isFullScreen));
                }
            }
            api.get_login_info(loginInfo => {
                if (loginInfo.session) {
                    // 这里需要注意，如果先dispatch登陆，再dispatch project id
                    // 会导致先进入showing_without_id 但不可创建
                    // 随后的登陆，会让canCreateNew由false变new，导致以为是创建新项目
                    // 更好的修改方向，应该是将两个事件包裹成一个来dispatch
                    this.store.dispatch(setProjectId(loginInfo.lastProjectId));
                    this.store.dispatch(setSession(loginInfo));
                } else {
                    this.store.dispatch(getNoSession());
                }
            },
            () => this.store.dispatch(getNoSession()));
        }

        componentDidUpdate (prevProps) {
            if (localesOnly) return;
            // Always sync isPlayerOnly and isFullScreen props to store state
            // This ensures they stay in sync even if other state changes trigger re-renders
            if (this.props.isPlayerOnly !== undefined) {
                const currentState = this.store.getState().scratchGui.mode.isPlayerOnly;
                if (currentState !== this.props.isPlayerOnly) {
                    this.store.dispatch(setPlayer(this.props.isPlayerOnly));
                }
            }
            if (this.props.isFullScreen !== undefined) {
                const currentState = this.store.getState().scratchGui.mode.isFullScreen;
                if (currentState !== this.props.isFullScreen) {
                    this.store.dispatch(setFullScreen(this.props.isFullScreen));
                }
            }
        }
        render () {
            const {
                isFullScreen, // eslint-disable-line no-unused-vars
                isPlayerOnly, // eslint-disable-line no-unused-vars
                showTelemetryModal, // eslint-disable-line no-unused-vars
                ...componentProps
            } = this.props;
            return (
                <Provider store={this.store}>
                    <ConnectedIntlProvider>
                        <WrappedComponent
                            {...componentProps}
                        />
                    </ConnectedIntlProvider>
                </Provider>
            );
        }
    }
    AppStateWrapper.propTypes = {
        isFullScreen: PropTypes.bool,
        isPlayerOnly: PropTypes.bool,
        isTelemetryEnabled: PropTypes.bool,
        showTelemetryModal: PropTypes.bool,
        session: PropTypes.object
    };
    return AppStateWrapper;
};

export default AppStateHOC;
